import React from 'react';
import { IMPLEMENTATION_LEVELS, POLICY_LEVELS, SCALE_LEVELS, STAKEHOLDER_TYPES } from '../data/lenses.js';
import { ACTIVITY_TYPOLOGY, THEME_TYPOLOGY, normalizeMainTheme, parseTypologyTag } from '../data/taxonomies.js';
import { AboutPage } from './about_page.jsx';
import { EmptyState, FilterChipRail } from './chips_related.jsx';
import { DirectoryHeroV2 } from './directory_hero_v2.jsx';
import { CountryFilter, FilterAccordion, SimpleFilter, TypologyPanel } from './filters.jsx';
import { DirectorySearchBar, Footer } from './footer.jsx';
import { OrgDetail } from './org_views.jsx';
import { SubmitPage } from './pages.jsx';
import { TypologyPage } from './typology_page.jsx';
import { ListView, MapView, WheelsView } from './views.jsx';
const { useState, useEffect, useMemo } = React;

// ============================================================
// Main New Economy Index prototype
// ============================================================

export const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "mapStyle": "geographic",
  "accentColor": "#D97757",
  "density": "comfortable",
  "showMapLabels": true,
  "showHero": true
}/*EDITMODE-END*/;

export function useRoute() {
  const parseHash = () => {
    const h = (window.location.hash || '#/').replace(/^#\/?/, '');
    if (!h || h === '') return 'directory';
    if (h.startsWith('about')) return 'about';
    if (h.startsWith('typology')) return 'typology';
    if (h.startsWith('submit')) return 'submit';
    return 'directory';
  };
  const [route, setRoute] = useState(parseHash);
  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const navigate = (r, opts) => {
    const map = { directory: '#/', about: '#/about', typology: '#/typology', submit: '#/submit' };
    let h = map[r] || '#/';
    if (opts && opts.view) h += (h.includes('?') ? '&' : '?') + 'view=' + opts.view;
    window.location.hash = h;
    window.scrollTo({top: 0, behavior: 'instant'});
  };
  return [route, navigate];
}

export function orgHasProfile(o) {
  // Org counts as "having information" if it has a mission AND at least one activity or theme tag.
  const hasMission = (o.mission || '').trim().length > 20;
  const hasTags = (o.activities && o.activities.length > 0) || (o.themes && o.themes.length > 0);
  return hasMission && hasTags;
}


export function App() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tweaks, setTweaks] = useState(TWEAK_DEFAULTS);
  const [tweaksVisible, setTweaksVisible] = useState(false);
  const [route, navigate] = useRoute();

  // ---- URL filter-state helpers ----
  const parseFilterQS = () => {
    const m = (window.location.hash || '').match(/\?(.+)$/);
    if (!m) return {};
    const out = {};
    for (const part of m[1].split('&')) {
      const [k, v] = part.split('=');
      if (!k) continue;
      out[decodeURIComponent(k)] = v ? decodeURIComponent(v) : '';
    }
    return out;
  };
  const setToQS = (s) => [...s].map(encodeURIComponent).join(',');
  const qsToSet = (s) => new Set((s || '').split(',').filter(Boolean).map(decodeURIComponent));

  useEffect(() => {
    const handler = (e) => {
      if (!e.data) return;
      if (e.data.type === '__activate_edit_mode') setTweaksVisible(true);
      if (e.data.type === '__deactivate_edit_mode') setTweaksVisible(false);
    };
    window.addEventListener('message', handler);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', handler);
  }, []);

  const updateTweak = (key, value) => {
    setTweaks(prev => ({ ...prev, [key]: value }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [key]: value } }, '*');
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--accent', tweaks.accentColor);
    document.body.dataset.density = tweaks.density;
  }, [tweaks.accentColor, tweaks.density]);

  // Filters
  const [view, setView] = useState(() => {
    const m = (window.location.hash || '').match(/view=(\w+)/);
    if (m && ['map','wheels','list'].includes(m[1])) return m[1];
    return 'map';
  }); // 'map' | 'wheels' | 'list'
  useEffect(() => {
    const handler = () => {
      const m = (window.location.hash || '').match(/view=(\w+)/);
      if (m && ['map','wheels','list'].includes(m[1])) setView(m[1]);
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);
  const [search, setSearch] = useState(() => parseFilterQS().q || '');
  const [selectedActivities, setSelectedActivities] = useState(() => qsToSet(parseFilterQS().activity));
  const [selectedThemes, setSelectedThemes] = useState(() => qsToSet(parseFilterQS().theme));
  const [selectedCountries, setSelectedCountries] = useState(() => qsToSet(parseFilterQS().country));
  // Helper: toggle a country in/out of the set
  const toggleCountry = (c) => {
    if (!c) { setSelectedCountries(new Set()); return; }
    setSelectedCountries(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };
  const [selectedScales, setSelectedScales] = useState(() => qsToSet(parseFilterQS().scale));
  const [selectedPolicyProximity, setSelectedPolicyProximity] = useState(() => qsToSet(parseFilterQS().policy));
  const [selectedImplementation, setSelectedImplementation] = useState(() => qsToSet(parseFilterQS().impl));
  const [selectedTypes, setSelectedTypes] = useState(() => qsToSet(parseFilterQS().type));
  const [yearRange, setYearRange] = useState([1880, 2026]);
  const [selectedStakeholders, setSelectedStakeholders] = useState(() => qsToSet(parseFilterQS().stakeholder));
  const [selectedOrg, setSelectedOrg] = useState(null);
  // Desktop opens with filters showing (unchanged); mobile starts collapsed so
  // the map/list/typologies are visible first (the panel is a full-width drawer).
  const [filtersOpen, setFiltersOpen] = useState(() => window.innerWidth > 768);
  const [sortBy, setSortBy] = useState('name-asc');

  // Sync filter state -> URL (debounced via effect)
  useEffect(() => {
    if (route !== 'directory') return;
    const params = [];
    if (search) params.push('q=' + encodeURIComponent(search));
    if (selectedCountries.size) params.push('country=' + setToQS(selectedCountries));
    if (selectedActivities.size) params.push('activity=' + setToQS(selectedActivities));
    if (selectedThemes.size) params.push('theme=' + setToQS(selectedThemes));
    if (selectedScales.size) params.push('scale=' + setToQS(selectedScales));
    if (selectedPolicyProximity.size) params.push('policy=' + setToQS(selectedPolicyProximity));
    if (selectedImplementation.size) params.push('impl=' + setToQS(selectedImplementation));
    if (selectedTypes.size) params.push('type=' + setToQS(selectedTypes));
    if (selectedStakeholders.size) params.push('stakeholder=' + setToQS(selectedStakeholders));
    if (view !== 'map') params.push('view=' + view);
    const baseHash = '#/';
    const newHash = params.length ? `${baseHash}?${params.join('&')}` : baseHash;
    if (window.location.hash !== newHash && window.location.hash.replace(/^#$/, '#/') !== newHash) {
      // Use replaceState to avoid polluting history on every chip toggle.
      const url = new URL(window.location.href);
      url.hash = newHash;
      window.history.replaceState(null, '', url);
    }
  }, [route, view, search, selectedCountries, selectedActivities, selectedThemes,
      selectedScales, selectedPolicyProximity, selectedImplementation, selectedTypes, selectedStakeholders]);

  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    fetch((window.__resources && window.__resources.orgsJson) || '/data/orgs.json')
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(data => {
      // Normalise the positioning strings into arrays of {level, label} so they can be filtered on
      const parsed = data.map(o => ({
        ...o,
        // Treat UK as a single country on the map & filter; sub-region tag moves to a new field.
        country: (o.country || '').startsWith('United Kingdom - ') ? 'United Kingdom' : o.country,
        ukRegion: (o.country || '').startsWith('United Kingdom - ') ? o.country.replace('United Kingdom - ', '') : null,
        policyLevels: (o.policy || '').split(';').map(s => s.trim()).filter(Boolean),
        implementationLevels: (o.implementation || '').split(';').map(s => s.trim()).filter(Boolean),
        powerLevels: (o.power || '').split(';').map(s => s.trim()).filter(Boolean),
      }));
      setOrgs(parsed);
      setLoading(false);
    }).catch(() => { setLoadError(true); setLoading(false); });
  }, []);

  // Taxonomy-matching helpers: an org "has" a sub-typology if its tag string contains that sub label
  const orgHasActivity = (org, sub) => org.activities.some(a => a.toLowerCase().includes(sub.toLowerCase()));
  const orgHasTheme = (org, sub) => org.themes.some(t => t.toLowerCase().includes(sub.toLowerCase()));
  const orgHasMainActivity = (org, main) => org.activities.some(a => (parseTypologyTag(a)?.main || '').toLowerCase() === main.toLowerCase());
  const orgHasMainTheme = (org, main) => org.themes.some(t => {
    const p = parseTypologyTag(t);
    return p && normalizeMainTheme(p.main).toLowerCase() === main.toLowerCase();
  });

  // Compute filtered orgs
  const filtered = useMemo(() => {
    if (!orgs.length) return [];
    const q = search.trim().toLowerCase();
    return orgs.filter(org => {
      if (q && !(org.name.toLowerCase().includes(q) || (org.mission || '').toLowerCase().includes(q))) return false;
      if (selectedCountries.size > 0 && !selectedCountries.has(org.country)) return false;
      if (selectedScales.size > 0 && !org.scale.some(s => selectedScales.has(s))) return false;
      if (selectedTypes.size > 0 && !selectedTypes.has(org.type)) return false;
      if (selectedStakeholders.size > 0 && !org.stakeholders.some(s => selectedStakeholders.has(s))) return false;
      if (selectedPolicyProximity.size > 0 && !(org.policyLevels || []).some(p => selectedPolicyProximity.has(p))) return false;
      if (selectedImplementation.size > 0 && !(org.implementationLevels || []).some(p => selectedImplementation.has(p))) return false;
      if (org.foundedYear && (org.foundedYear < yearRange[0] || org.foundedYear > yearRange[1])) return false;

      // Activities: if any selected, org must match at least one (sub or main)
      if (selectedActivities.size > 0) {
        const any = [...selectedActivities].some(a => {
          // Check if it's a main category name
          const mainCat = ACTIVITY_TYPOLOGY.find(c => c.name === a);
          if (mainCat) return orgHasMainActivity(org, a);
          return orgHasActivity(org, a);
        });
        if (!any) return false;
      }
      if (selectedThemes.size > 0) {
        const any = [...selectedThemes].some(t => {
          const mainCat = THEME_TYPOLOGY.find(c => c.name === t);
          if (mainCat) return orgHasMainTheme(org, t);
          return orgHasTheme(org, t);
        });
        if (!any) return false;
      }
      return true;
    });
  }, [orgs, search, selectedCountries, selectedScales, selectedTypes, selectedStakeholders, selectedPolicyProximity, selectedImplementation, selectedActivities, selectedThemes, yearRange]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    // Country counts inside the current filtered set (for country-most / country-least).
    const countryCounts = {};
    arr.forEach(o => {
      const k = o.country || 'Other';
      countryCounts[k] = (countryCounts[k] || 0) + 1;
    });
    const firstAct = (o) => (o.activities && o.activities[0]) || '\uFFFF';
    const firstTheme = (o) => (o.themes && o.themes[0]) || '\uFFFF';
    const cmp =
        sortBy === 'name-asc'      ? (a,b) => a.name.localeCompare(b.name)
      : sortBy === 'name-desc'     ? (a,b) => b.name.localeCompare(a.name)
      : sortBy === 'country-most'  ? (a,b) => (countryCounts[b.country || 'Other'] - countryCounts[a.country || 'Other']) || (a.country || '').localeCompare(b.country || '') || a.name.localeCompare(b.name)
      : sortBy === 'country-least' ? (a,b) => (countryCounts[a.country || 'Other'] - countryCounts[b.country || 'Other']) || (a.country || '').localeCompare(b.country || '') || a.name.localeCompare(b.name)
      : sortBy === 'activity'      ? (a,b) => firstAct(a).localeCompare(firstAct(b)) || a.name.localeCompare(b.name)
      : sortBy === 'theme'         ? (a,b) => firstTheme(a).localeCompare(firstTheme(b)) || a.name.localeCompare(b.name)
      : (a,b) => a.name.localeCompare(b.name);
    arr.sort((a, b) => {
      const pa = orgHasProfile(a), pb = orgHasProfile(b);
      if (pa !== pb) return pa ? -1 : 1;
      return cmp(a, b);
    });
    return arr;
  }, [filtered, sortBy]);

  // Country counts (based on filters EXCEPT country, so map stays interactive)
  const mapCounts = useMemo(() => {
    const counts = {};
    const q = search.trim().toLowerCase();
    for (const org of orgs) {
      if (q && !(org.name.toLowerCase().includes(q) || (org.mission || '').toLowerCase().includes(q))) continue;
      if (selectedScales.size > 0 && !org.scale.some(s => selectedScales.has(s))) continue;
      if (selectedTypes.size > 0 && !selectedTypes.has(org.type)) continue;
      if (selectedStakeholders.size > 0 && !org.stakeholders.some(s => selectedStakeholders.has(s))) continue;
      if (selectedPolicyProximity.size > 0 && !(org.policyLevels || []).some(p => selectedPolicyProximity.has(p))) continue;
      if (selectedImplementation.size > 0 && !(org.implementationLevels || []).some(p => selectedImplementation.has(p))) continue;
      if (org.foundedYear && (org.foundedYear < yearRange[0] || org.foundedYear > yearRange[1])) continue;
      if (selectedActivities.size > 0) {
        const any = [...selectedActivities].some(a => {
          const mainCat = ACTIVITY_TYPOLOGY.find(c => c.name === a);
          if (mainCat) return orgHasMainActivity(org, a);
          return orgHasActivity(org, a);
        });
        if (!any) continue;
      }
      if (selectedThemes.size > 0) {
        const any = [...selectedThemes].some(t => {
          const mainCat = THEME_TYPOLOGY.find(c => c.name === t);
          if (mainCat) return orgHasMainTheme(org, t);
          return orgHasTheme(org, t);
        });
        if (!any) continue;
      }
      counts[org.country] = (counts[org.country] || 0) + 1;
    }
    return counts;
  }, [orgs, search, selectedScales, selectedTypes, selectedStakeholders, selectedPolicyProximity, selectedImplementation, selectedActivities, selectedThemes, yearRange]);

  const maxCount = Math.max(...Object.values(mapCounts), 1);

  // Per-option counts: for each filter, count orgs that pass all OTHER filters.
  // Lets each filter option show a "this many would match if you add me" hint.
  const optionCounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const passOthers = (org, exclude) => {
      if (q && !(org.name.toLowerCase().includes(q) || (org.mission || '').toLowerCase().includes(q))) return false;
      if (exclude !== 'country' && selectedCountries.size > 0 && !selectedCountries.has(org.country)) return false;
      if (exclude !== 'scale' && selectedScales.size > 0 && !org.scale.some(s => selectedScales.has(s))) return false;
      if (exclude !== 'type' && selectedTypes.size > 0 && !selectedTypes.has(org.type)) return false;
      if (exclude !== 'stakeholder' && selectedStakeholders.size > 0 && !org.stakeholders.some(s => selectedStakeholders.has(s))) return false;
      if (exclude !== 'policy' && selectedPolicyProximity.size > 0 && !(org.policyLevels || []).some(p => selectedPolicyProximity.has(p))) return false;
      if (exclude !== 'impl' && selectedImplementation.size > 0 && !(org.implementationLevels || []).some(p => selectedImplementation.has(p))) return false;
      if (org.foundedYear && (org.foundedYear < yearRange[0] || org.foundedYear > yearRange[1])) return false;
      if (exclude !== 'activity' && selectedActivities.size > 0) {
        const any = [...selectedActivities].some(a => {
          const mainCat = ACTIVITY_TYPOLOGY.find(c => c.name === a);
          if (mainCat) return orgHasMainActivity(org, a);
          return orgHasActivity(org, a);
        });
        if (!any) return false;
      }
      if (exclude !== 'theme' && selectedThemes.size > 0) {
        const any = [...selectedThemes].some(t => {
          const mainCat = THEME_TYPOLOGY.find(c => c.name === t);
          if (mainCat) return orgHasMainTheme(org, t);
          return orgHasTheme(org, t);
        });
        if (!any) return false;
      }
      return true;
    };
    const out = {
      country: {}, scale: {}, type: {}, stakeholder: {}, policy: {}, impl: {},
      activitySub: {}, activityMain: {}, themeSub: {}, themeMain: {},
    };
    for (const org of orgs) {
      // country
      if (passOthers(org, 'country') && org.country) out.country[org.country] = (out.country[org.country] || 0) + 1;
      // scale
      if (passOthers(org, 'scale')) for (const s of org.scale) out.scale[s] = (out.scale[s] || 0) + 1;
      // type
      if (passOthers(org, 'type') && org.type) out.type[org.type] = (out.type[org.type] || 0) + 1;
      // stakeholder
      if (passOthers(org, 'stakeholder')) for (const s of org.stakeholders) out.stakeholder[s] = (out.stakeholder[s] || 0) + 1;
      // policy
      if (passOthers(org, 'policy')) for (const p of (org.policyLevels || [])) out.policy[p] = (out.policy[p] || 0) + 1;
      // impl
      if (passOthers(org, 'impl')) for (const p of (org.implementationLevels || [])) out.impl[p] = (out.impl[p] || 0) + 1;
      // activity sub + main
      if (passOthers(org, 'activity')) {
        const subs = new Set(); const mains = new Set();
        for (const a of org.activities) {
          const p = parseTypologyTag(a);
          if (p?.sub) subs.add(p.sub);
          if (p?.main) mains.add(p.main);
        }
        for (const s of subs) out.activitySub[s] = (out.activitySub[s] || 0) + 1;
        for (const m of mains) out.activityMain[m] = (out.activityMain[m] || 0) + 1;
      }
      // theme sub + main
      if (passOthers(org, 'theme')) {
        const subs = new Set(); const mains = new Set();
        for (const t of org.themes) {
          const p = parseTypologyTag(t);
          if (p?.sub) subs.add(p.sub);
          if (p?.main) mains.add(normalizeMainTheme(p.main));
        }
        for (const s of subs) out.themeSub[s] = (out.themeSub[s] || 0) + 1;
        for (const m of mains) out.themeMain[m] = (out.themeMain[m] || 0) + 1;
      }
    }
    return out;
  }, [orgs, search, selectedCountries, selectedScales, selectedTypes, selectedStakeholders,
      selectedPolicyProximity, selectedImplementation, selectedActivities, selectedThemes, yearRange]);

  // Filter toggles
  const toggleSet = (set, setFn) => (val) => {
    setFn(prev => {
      const n = new Set(prev);
      if (n.has(val)) n.delete(val); else n.add(val);
      return n;
    });
  };
  const toggleActivity = toggleSet(selectedActivities, setSelectedActivities);
  const toggleTheme = toggleSet(selectedThemes, setSelectedThemes);
  const toggleMainActivity = (cat) => {
    setSelectedActivities(prev => {
      const n = new Set(prev);
      const allSelected = cat.sub.every(s => n.has(s));
      if (allSelected || n.has(cat.name)) {
        cat.sub.forEach(s => n.delete(s));
        n.delete(cat.name);
      } else {
        cat.sub.forEach(s => n.add(s));
        n.add(cat.name);
      }
      return n;
    });
  };
  const toggleMainTheme = (cat) => {
    setSelectedThemes(prev => {
      const n = new Set(prev);
      const allSelected = cat.sub.every(s => n.has(s));
      if (allSelected || n.has(cat.name)) {
        cat.sub.forEach(s => n.delete(s));
        n.delete(cat.name);
      } else {
        cat.sub.forEach(s => n.add(s));
        n.add(cat.name);
      }
      return n;
    });
  };

  const allScales = SCALE_LEVELS;

  const allTypes = useMemo(() => {
    const s = new Set();
    orgs.forEach(o => o.type && s.add(o.type));
    return [...s].sort();
  }, [orgs]);

  const allCountries = useMemo(() => {
    const map = new Map();
    orgs.forEach(o => {
      if (!o.country) return;
      map.set(o.country, (map.get(o.country) || 0) + 1);
    });
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count, label: name }));
  }, [orgs]);

  // Countries with live (filtered) counts; preserves original sort order from full universe
  const countriesWithLiveCounts = useMemo(() => {
    return allCountries.map(c => ({
      ...c,
      count: (optionCounts.country && optionCounts.country[c.name]) || 0,
    }));
  }, [allCountries, optionCounts]);

  const totalActive = selectedActivities.size + selectedThemes.size + selectedScales.size + selectedPolicyProximity.size + selectedImplementation.size + selectedTypes.size + selectedStakeholders.size 
    + selectedCountries.size + (search ? 1 : 0);
  const clearAll = () => {
    setSelectedActivities(new Set()); setSelectedThemes(new Set());
    setSelectedScales(new Set()); setSelectedPolicyProximity(new Set()); setSelectedImplementation(new Set());
    setSelectedTypes(new Set());
    setSelectedStakeholders(new Set()); setSelectedCountries(new Set());
    setSearch(''); setYearRange([1880, 2026]);
  };

  if (loadError) return (
    <div className="dir-status">
      <div className="dir-status-inner">
        <div className="dir-status-icon" aria-hidden="true">
          <svg viewBox="0 0 48 48" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M24 6L4 42h40L24 6z"/><path d="M24 20v10"/><circle cx="24" cy="36" r="0.8" fill="currentColor"/></svg>
        </div>
        <h2 className="dir-status-h">We couldn't load the directory.</h2>
        <p className="dir-status-p">Something went wrong fetching the organisations. Check your connection and try again.</p>
        <button className="btn-primary" onClick={() => window.location.reload()}>Reload</button>
      </div>
    </div>
  );
  if (loading) return (
    <div className="dir-status">
      <div className="dir-status-inner">
        <div className="dir-status-spinner" aria-hidden="true"/>
        <h2 className="dir-status-h">Loading the directory…</h2>
        <p className="dir-status-p">Gathering organisations across Europe.</p>
      </div>
    </div>
  );

  const headerProps = { search, setSearch, totalActive, clearAll, count: filtered.length, total: orgs.length, route, onNavigate: navigate };

  if (route === 'about') {
    return (
      <div className="app app-static">
        <Header {...headerProps} />
        <main className="static-main"><AboutPage onNavigate={navigate} /></main>
        <Footer onNavigate={navigate} />
        {tweaksVisible && <TweaksPanel tweaks={tweaks} onChange={updateTweak} onClose={() => setTweaksVisible(false)} />}
      </div>
    );
  }
  if (route === 'typology') {
    return (
      <div className="app app-static">
        <Header {...headerProps} />
        <main className="static-main"><TypologyPage onNavigate={navigate} /></main>
        <Footer onNavigate={navigate} />
        {tweaksVisible && <TweaksPanel tweaks={tweaks} onChange={updateTweak} onClose={() => setTweaksVisible(false)} />}
      </div>
    );
  }
  if (route === 'submit') {
    return (
      <div className="app app-static">
        <Header {...headerProps} />
        <main className="static-main"><SubmitPage onNavigate={navigate} orgs={orgs} /></main>
        <Footer onNavigate={navigate} />
        {tweaksVisible && <TweaksPanel tweaks={tweaks} onChange={updateTweak} onClose={() => setTweaksVisible(false)} />}
      </div>
    );
  }

  return (
    <div className="app app-directory">
      <Header {...headerProps}/>
      {tweaks.showHero && <DirectoryHeroV2 orgs={orgs} onNavigate={navigate} onSelectOrg={setSelectedOrg} />}
      <DirectorySearchBar search={search} setSearch={setSearch} />
      <div className="main-grid">
        <aside className={`sidebar ${filtersOpen ? 'open' : 'closed'}`}>
          <button className="sidebar-toggle" onClick={() => setFiltersOpen(!filtersOpen)}>
            <span className="toggle-label-desktop">{filtersOpen ? '‹ Hide filters' : '›'}</span>
            <span className="toggle-label-mobile">{filtersOpen ? 'Hide filters ✕' : '☰ Filters'}</span>
          </button>
          {filtersOpen && <div className="sidebar-inner">
            <div className="sidebar-headline">
              <span className="filter-h">Filters</span>
              {totalActive > 0 && (
                <button type="button" className="filter-clear-mini" onClick={clearAll}>Clear {totalActive}</button>
              )}
            </div>

            <FilterAccordion title="Activities" activeCount={selectedActivities.size}
              defaultOpen={true}
              hint="What organisations do — primary lens.">
              <TypologyPanel typology={ACTIVITY_TYPOLOGY} selected={selectedActivities}
                onToggle={toggleActivity} onToggleMain={toggleMainActivity} title=""
                subCounts={optionCounts.activitySub} mainCounts={optionCounts.activityMain} />
            </FilterAccordion>

            <FilterAccordion title="Themes" activeCount={selectedThemes.size}
              defaultOpen={true}
              hint="What they work on — primary lens.">
              <TypologyPanel typology={THEME_TYPOLOGY} selected={selectedThemes}
                onToggle={toggleTheme} onToggleMain={toggleMainTheme} title=""
                subCounts={optionCounts.themeSub} mainCounts={optionCounts.themeMain} />
            </FilterAccordion>

            <FilterAccordion title="Country" activeCount={selectedCountries.size}>
              {selectedCountries.size > 0 && (
                <div className="active-countries-list">
                  {[...selectedCountries].map(c => (
                    <div key={c} className="active-country">
                      <span>📍 {c}</span>
                      <button onClick={() => toggleCountry(c)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <CountryFilter countries={countriesWithLiveCounts} selected={selectedCountries} onToggle={toggleCountry}/>
            </FilterAccordion>

            <FilterAccordion title="Geographic scale" activeCount={selectedScales.size}
              hint="Where the organisation operates — local to global.">
              <SimpleFilter label="" options={allScales}
                selected={selectedScales} onToggle={toggleSet(selectedScales, setSelectedScales)}
                counts={optionCounts.scale} />
            </FilterAccordion>

            <FilterAccordion title="Policy proximity" activeCount={selectedPolicyProximity.size}
              hint="Distance from policy — from distant to actor.">
              <SimpleFilter label="" options={POLICY_LEVELS}
                selected={selectedPolicyProximity} onToggle={toggleSet(selectedPolicyProximity, setSelectedPolicyProximity)}
                counts={optionCounts.policy} />
            </FilterAccordion>

            <FilterAccordion title="Implementation proximity" activeCount={selectedImplementation.size}
              hint="From thinking/advocating to on-the-ground operator.">
              <SimpleFilter label="" options={IMPLEMENTATION_LEVELS}
                selected={selectedImplementation} onToggle={toggleSet(selectedImplementation, setSelectedImplementation)}
                counts={optionCounts.impl} />
            </FilterAccordion>

            <FilterAccordion title="Stakeholders engaged" activeCount={selectedStakeholders.size}
              hint="Which groups an organisation works with.">
              <SimpleFilter label="" options={STAKEHOLDER_TYPES}
                selected={selectedStakeholders} onToggle={toggleSet(selectedStakeholders, setSelectedStakeholders)}
                counts={optionCounts.stakeholder} />
            </FilterAccordion>

            <FilterAccordion title="Organisation type" activeCount={selectedTypes.size}>
              <SimpleFilter label="" options={allTypes}
                selected={selectedTypes} onToggle={toggleSet(selectedTypes, setSelectedTypes)}
                counts={optionCounts.type} />
            </FilterAccordion>

            <FilterAccordion title="Founded">
              <YearRange value={yearRange} onChange={setYearRange} orgs={orgs}/>
            </FilterAccordion>
          </div>}
        </aside>

        <main className="content">
          <FilterChipRail
            search={search} setSearch={setSearch}
            selectedActivities={selectedActivities} toggleActivity={toggleActivity} toggleMainActivity={toggleMainActivity}
            selectedThemes={selectedThemes} toggleTheme={toggleTheme} toggleMainTheme={toggleMainTheme}
            selectedCountries={selectedCountries} toggleCountry={toggleCountry}
            selectedScales={selectedScales} setSelectedScales={setSelectedScales}
            selectedPolicyProximity={selectedPolicyProximity} setSelectedPolicyProximity={setSelectedPolicyProximity}
            selectedImplementation={selectedImplementation} setSelectedImplementation={setSelectedImplementation}
            selectedTypes={selectedTypes} setSelectedTypes={setSelectedTypes}
            selectedStakeholders={selectedStakeholders} setSelectedStakeholders={setSelectedStakeholders}
            clearAll={clearAll}
          />
          <div className="view-toolbar">
            <div className="view-tabs">
              <button className={view === 'map' ? 'active' : ''} onClick={() => setView('map')}>
                <svg viewBox="0 0 16 16" width="13" height="13"><path d="M1 3l4-1 6 2 4-1v11l-4 1-6-2-4 1z M5 2v11 M11 4v11" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>
                Map
              </button>
              <button className={view === 'wheels' ? 'active' : ''} onClick={() => setView('wheels')}>
                <svg viewBox="0 0 16 16" width="13" height="13"><circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.2"/><circle cx="8" cy="8" r="2" fill="none" stroke="currentColor" strokeWidth="1.2"/><path d="M8 1.5v5 M8 9.5v5 M1.5 8h5 M9.5 8h5" stroke="currentColor" strokeWidth="1.2"/></svg>
                Typologies
              </button>
              <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
                <svg viewBox="0 0 16 16" width="13" height="13"><path d="M2 4h12 M2 8h12 M2 12h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                List
              </button>
            </div>
            <div className="view-toolbar-right">
              <span className="result-count">{filtered.length} organisations</span>
              <select className="sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="name-asc">Default (Alphabetical, A–Z)</option>
                <option value="name-desc">Default (Alphabetical, Z–A)</option>
                <option value="country-most">Country (Most–least)</option>
                <option value="country-least">Country (Least–most)</option>
                <option value="activity">Activity (A–Z)</option>
                <option value="theme">Theme (A–Z)</option>
              </select>
            </div>
          </div>

          {(() => {
            // Build the same chip set the rail uses, for the empty state to suggest removals.
            const chips = [];
            const fmtLens = v => { const m = v.match(/^(\d+)\s*-\s*(.+)$/); return m ? `${m[1]}. ${m[2]}` : v; };
            const removeFromSet = setFn => val => setFn(prev => { const n = new Set(prev); n.delete(val); return n; });
            if (search) chips.push({ kind: 'search', label: `"${search}"`, onRemove: () => setSearch('') });
            selectedActivities.forEach(a => {
              const mainCat = ACTIVITY_TYPOLOGY.find(c => c.name === a);
              chips.push({ kind: 'activity', label: a, onRemove: () => mainCat ? toggleMainActivity(mainCat) : toggleActivity(a) });
            });
            selectedThemes.forEach(t => {
              const mainCat = THEME_TYPOLOGY.find(c => c.name === t);
              chips.push({ kind: 'theme', label: t, onRemove: () => mainCat ? toggleMainTheme(mainCat) : toggleTheme(t) });
            });
            selectedCountries.forEach(c => chips.push({ kind: 'country', label: c, onRemove: () => toggleCountry(c) }));
            selectedScales.forEach(s => chips.push({ kind: 'scale', label: s, onRemove: () => removeFromSet(setSelectedScales)(s) }));
            selectedPolicyProximity.forEach(p => chips.push({ kind: 'policy', label: fmtLens(p), onRemove: () => removeFromSet(setSelectedPolicyProximity)(p) }));
            selectedImplementation.forEach(p => chips.push({ kind: 'implementation', label: fmtLens(p), onRemove: () => removeFromSet(setSelectedImplementation)(p) }));
            selectedTypes.forEach(t => chips.push({ kind: 'type', label: t, onRemove: () => removeFromSet(setSelectedTypes)(t) }));
            selectedStakeholders.forEach(s => chips.push({ kind: 'stakeholder', label: s, onRemove: () => removeFromSet(setSelectedStakeholders)(s) }));
            const emptyEl = <EmptyState activeFilterChips={chips} clearAll={clearAll} onNavigate={navigate} />;
            return (
              <>
                {view === 'map' && <MapView
                  orgs={sorted} counts={mapCounts} maxCount={maxCount}
                  selectedCountries={selectedCountries} onToggleCountry={toggleCountry}
                  onSelectOrg={setSelectedOrg}
                  mapStyle={tweaks.mapStyle} accentColor={tweaks.accentColor}
                  emptyState={emptyEl} />}
                {view === 'wheels' && <WheelsView
                  selectedActivities={selectedActivities} selectedThemes={selectedThemes}
                  toggleActivity={toggleActivity} toggleTheme={toggleTheme}
                  toggleMainActivity={toggleMainActivity} toggleMainTheme={toggleMainTheme}
                  orgs={sorted} allOrgs={orgs} onSelectOrg={setSelectedOrg} />}
                {view === 'list' && <ListView orgs={sorted} onSelectOrg={setSelectedOrg} emptyState={emptyEl} sortBy={sortBy} />}
              </>
            );
          })()}
        </main>
      </div>
      {selectedOrg && <OrgDetail org={selectedOrg} onClose={() => setSelectedOrg(null)} allOrgs={orgs} onSelectOrg={setSelectedOrg} />}
      <Footer onNavigate={navigate} />
      {tweaksVisible && <TweaksPanel tweaks={tweaks} onChange={updateTweak} onClose={() => setTweaksVisible(false)} />}
    </div>
  );
}

export function TweaksPanel({ tweaks, onChange, onClose }) {
  const accents = [
    { label: 'Clay', color: '#D97757' },
    { label: 'Amber', color: '#D89838' },
    { label: 'Olive', color: '#7C8D4C' },
    { label: 'Teal', color: '#468C8C' },
    { label: 'Plum', color: '#8A4D7C' },
    { label: 'Ink', color: '#3A3A3A' },
  ];
  return (
    <div className="tweaks-panel">
      <div className="tweaks-head">
        <div className="tweaks-title">Tweaks</div>
        <button className="tweaks-close" onClick={onClose}>✕</button>
      </div>
      <div className="tweak-group">
        <div className="tweak-label">Map style</div>
        <div className="tweak-radio">
          <button className={tweaks.mapStyle === 'geographic' ? 'on' : ''} onClick={() => onChange('mapStyle', 'geographic')}>Geographic Europe</button>
          <button className={tweaks.mapStyle === 'hex' ? 'on' : ''} onClick={() => onChange('mapStyle', 'hex')}>Hex tile map</button>
        </div>
      </div>
      <div className="tweak-group">
        <div className="tweak-label">Accent colour</div>
        <div className="tweak-swatches">
          {accents.map(a => (
            <button key={a.label} className={`swatch ${tweaks.accentColor === a.color ? 'on' : ''}`}
                    style={{ background: a.color }} onClick={() => onChange('accentColor', a.color)} title={a.label}/>
          ))}
        </div>
      </div>
      <div className="tweak-group">
        <div className="tweak-label">Density</div>
        <div className="tweak-radio">
          <button className={tweaks.density === 'comfortable' ? 'on' : ''} onClick={() => onChange('density', 'comfortable')}>Comfortable</button>
          <button className={tweaks.density === 'compact' ? 'on' : ''} onClick={() => onChange('density', 'compact')}>Compact</button>
        </div>
      </div>
    </div>
  );
}

export function Header({ search, setSearch, totalActive, clearAll, count, total, route, onNavigate }) {
  return (
    <header className="header">
      <a className="header-brand" href="#/" onClick={(e) => { e.preventDefault(); onNavigate('directory'); }}>
        <img src="/assets/logo-box-honey.png" alt="New Economy Index" className="logo-img"/>
      </a>
      <nav className="header-nav">
        <a className={`nav-link ${route === 'directory' ? 'active' : ''}`} href="#/" onClick={(e) => { e.preventDefault(); onNavigate('directory'); }}>Directory</a>
        <a className={`nav-link ${route === 'typology' ? 'active' : ''}`} href="#/typology" onClick={(e) => { e.preventDefault(); onNavigate('typology'); }}>Categorisation</a>
        <a className={`nav-link ${route === 'about' ? 'active' : ''}`} href="#/about" onClick={(e) => { e.preventDefault(); onNavigate('about'); }}>About</a>
      </nav>
      <div className="header-right">
        {route === 'directory' && totalActive > 0 && <button className="clear-all" onClick={clearAll}>Clear ({totalActive})</button>}
        <a className="btn-primary btn-sm" href="#/submit" onClick={(e) => { e.preventDefault(); onNavigate('submit'); }}>Add / update your org</a>
      </div>
    </header>
  );
}

export function YearRange({ value, onChange, orgs }) {
  const [min, max] = [1880, 2026];
  return (
    <div className="year-range">
      <div className="year-inputs">
        <input type="number" min={min} max={value[1]} value={value[0]} onChange={e => onChange([+e.target.value, value[1]])}/>
        <span>to</span>
        <input type="number" min={value[0]} max={max} value={value[1]} onChange={e => onChange([value[0], +e.target.value])}/>
      </div>
    </div>
  );
}

export default App;
